import { callApi } from "@/services/api"
import { ActionContext } from "vuex"
import {
  WheelOfFortuneState,
  RootState,
  WheelOfFortuneRoomsAsMap,
} from "@/store/types"
import {
  GetRoomsQuery,
  Pagination,
  RoomCreateItem,
  WheelOfFortuneRoom,
} from "@/services/types"
import { arrayToMap, mergeMaps } from "@/store/utils/rooms"
import { CasinoGameSlug, GameSlug, RoomStatuses } from "@/services/enums"

type GetRoomsPayload = {
  game_slug: GameSlug
  loadNextPage: boolean
  forPage: "default" | "my-games" | "history"
}

const queryByFilter = {
  default: {} as GetRoomsQuery,
  "my-games": {
    statusIncludes: [
      RoomStatuses.finished,
      RoomStatuses.pending,
      RoomStatuses.started,
    ],
    isUserInRoom: true,
  } as GetRoomsQuery,
  history: {
    statusIncludes: [RoomStatuses.finished],
  } as GetRoomsQuery,
}

type GetRoomPayload = {
  game_slug: GameSlug
  room_id: number
}

export type LeaveRoomPayload = {
  game_slug: GameSlug
  room_id: number
}

type RoomsResult = {
  data: WheelOfFortuneRoomsAsMap
  pagination?: Pagination
}

type RoomResult = {
  data: WheelOfFortuneRoom | null
}

type InviteBotResult = {
  data: any
}

export async function inviteBot(
  context: ActionContext<WheelOfFortuneState, RootState>,
  payload: LeaveRoomPayload
): Promise<InviteBotResult> {
  const { room_id, game_slug } = payload
  const response = await callApi.inviteBot<WheelOfFortuneRoom>(
    game_slug,
    CasinoGameSlug.WheelOfFortune,
    room_id
  )

  return {
    data: response.data.data,
  }
}

export async function getRooms(
  context: ActionContext<WheelOfFortuneState, RootState>,
  payload: GetRoomsPayload
): Promise<RoomsResult> {
  const { game_slug, loadNextPage, forPage } = payload

  const page =
    loadNextPage && context.state.rooms.pagination
      ? context.state.rooms.pagination.next
      : undefined

  const response = await callApi.getRooms<WheelOfFortuneRoom>(
    game_slug,
    CasinoGameSlug.WheelOfFortune,
    page,
    queryByFilter[forPage]
  )
  const responseDataToMap = arrayToMap(response.data.data, "id")

  return {
    data: page
      ? mergeMaps(context.state.rooms.data, responseDataToMap)
      : responseDataToMap,
    pagination: response.data.links,
  }
}

export async function getRoom(
  context: ActionContext<WheelOfFortuneState, RootState>,
  payload: GetRoomPayload
): Promise<RoomResult> {
  const { game_slug, room_id } = payload

  const response = await callApi.getRoom<WheelOfFortuneRoom>(
    game_slug,
    CasinoGameSlug.WheelOfFortune,
    room_id
  )

  return {
    data: response.data.data,
  }
}

export async function leaveRoom(
  context: ActionContext<WheelOfFortuneState, RootState>,
  payload: LeaveRoomPayload
): Promise<RoomResult> {
  const { game_slug, room_id } = payload

  const response = await callApi.leaveRoom<WheelOfFortuneRoom>(
    game_slug,
    CasinoGameSlug.WheelOfFortune,
    room_id
  )

  return {
    data: response.data.data.participants.length ? response.data.data : null,
  }
}

type JoinOrUpdateRoomPayload = {
  room_id: number
  game_slug: GameSlug
  casino_game_slug: CasinoGameSlug
  items: RoomCreateItem[]
}

export async function joinRoom(
  context: ActionContext<WheelOfFortuneState, RootState>,
  payload: JoinOrUpdateRoomPayload
): Promise<RoomResult> {
  const { room_id, game_slug, casino_game_slug, items } = payload

  const response = await callApi.joinRoom<WheelOfFortuneRoom>(
    room_id,
    game_slug,
    casino_game_slug,
    items
  )

  return {
    data: response.data.data,
  }
}

export async function updateRoom(
  context: ActionContext<WheelOfFortuneState, RootState>,
  payload: JoinOrUpdateRoomPayload
): Promise<RoomResult> {
  const { room_id, game_slug, casino_game_slug, items } = payload

  const response = await callApi.updateItems<WheelOfFortuneRoom>(
    room_id,
    game_slug,
    casino_game_slug,
    items
  )

  return {
    data: response.data.data,
  }
}
